import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { isInternal } from "@/lib/api";
import { DateInput, fmtDate } from "@/components/DateInput";
import { fl } from "@/lib/finI18n";
import { AttachmentPreview } from "@/components/AttachmentPreview";
import { exportRows } from "@/components/TableTools";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Download, FolderArchive, Eye, PackageOpen, Loader2, Search, FileSpreadsheet } from "lucide-react";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function Arsip() {
  const { user, selectedClientId } = useAuth();
  const { t, lang } = useI18n();
  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [zipping, setZipping] = useState(false);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (isInternal(user.role)) p.set("client_id", clientId);
    if (year !== "all") p.set("year", year);
    if (month !== "all") p.set("month", month);
    return p;
  }, [clientId, year, month, user.role]);

  const load = useCallback(async () => {
    if (!clientId) return;
    const { data } = await api.get(`/attachments?${params().toString()}`);
    setItems(data);
  }, [clientId, params]);
  useEffect(() => { load(); }, [load]);

  const save = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url; el.download = name; el.click();
    URL.revokeObjectURL(url);
  };
  const download = async (a) => {
    try { const res = await api.get(`/attachments/${a.id}/download`, { responseType: "blob" }); save(res.data, a.original_filename); }
    catch (e) { toast.error("Gagal mengunduh"); }
  };
  const bulk = async () => {
    const p = params();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    setZipping(true);
    try {
      const res = await api.get(`/attachments/bulk-download?${p.toString()}`, { responseType: "blob" });
      save(res.data, `lampiran_${dateFrom || (year !== "all" ? year : "semua")}${month !== "all" ? `-${String(month).padStart(2, "0")}` : ""}${dateTo ? `_${dateTo}` : ""}.zip`);
      toast.success("ZIP lampiran diunduh");
    } catch (e) { toast.error(e.response?.status === 404 ? "Tidak ada lampiran pada periode ini" : "Gagal mengunduh ZIP"); }
    finally { setZipping(false); }
  };

  const ql = q.trim().toLowerCase();
  const shown = items.filter((a) => (!dateFrom || (a.txn_date || "") >= dateFrom) && (!dateTo || (a.txn_date || "") <= dateTo)
    && (!ql || `${a.original_filename} ${a.invoice_no || ""} ${a.vendor || ""} ${a.txn_date || ""}`.toLowerCase().includes(ql)));
  const exportList = () => exportRows(shown, [["Periode", "period"], ["Tanggal", "txn_date"], ["File", "original_filename"], ["Invoice", "invoice_no"], ["Vendor", "vendor"]], "arsip_dokumen");
  const grouped = shown.reduce((acc, a) => { const k = a.period || "Tanpa Periode"; (acc[k] = acc[k] || []).push(a); return acc; }, {});
  const years = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-4" data-testid="arsip-page">
      <div className="page-sticky flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("arsip.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="arsip-count">Dokumen pendukung dikelompokkan per periode ({shown.length} file).</p>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder={fl("tSearch", lang)} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 w-44" data-testid="arsip-search" /></div>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-36" data-testid="arsip-month"><SelectValue placeholder="Bulan" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Bulan</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28" data-testid="arsip-year"><SelectValue placeholder="Tahun" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="space-y-1"><label className="text-[11px] text-muted-foreground">{fl("from", lang)}</label><DateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-9" data-testid="arsip-date-from" /></div>
          <div className="space-y-1"><label className="text-[11px] text-muted-foreground">{fl("to", lang)}</label><DateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-9" data-testid="arsip-date-to" /></div>
          <Button variant="outline" onClick={exportList} disabled={shown.length === 0} data-testid="arsip-export"><FileSpreadsheet className="h-4 w-4 mr-1.5" />{fl("tExport", lang)}</Button>
          <Button onClick={bulk} disabled={zipping || shown.length === 0} data-testid="arsip-bulk-download">
            {zipping ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PackageOpen className="h-4 w-4 mr-1.5" />}{fl("bulkDownload", lang)}
          </Button>
        </div>
      </div>

      {Object.keys(grouped).length === 0 && (
        <Card className="p-12 text-center text-muted-foreground">
          <FolderArchive className="h-10 w-10 mx-auto mb-3 opacity-40" />
          Belum ada dokumen arsip.
        </Card>
      )}

      {Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0])).map(([period, files]) => (
        <Card key={period} className="p-4">
          <h2 className="font-head font-semibold mb-3">{period} <span className="text-muted-foreground font-normal text-sm">· {files.length} dokumen</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {files.map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-md border border-border p-2.5 hover:bg-secondary/40 transition-colors" data-testid={`arsip-file-${a.id}`}>
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setPreview(a)} data-testid={`arsip-open-${a.id}`}>
                  <p className="text-xs font-medium truncate">{a.original_filename}</p>
                  <p className="text-[10px] text-muted-foreground">{fmtDate(a.txn_date)} · {a.invoice_no || a.vendor || "—"}</p>
                </button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title={fl("view", lang)} onClick={() => setPreview(a)} data-testid={`arsip-view-${a.id}`}><Eye className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title={fl("download", lang)} onClick={() => download(a)} data-testid={`arsip-download-${a.id}`}><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </Card>
      ))}
      <AttachmentPreview att={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
